import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { gql, useQuery } from "@apollo/client";

import { Error } from "src/components/error/error";
import { Loading } from "src/components/loading/loading";
import { ReportSelector } from "src/components/report_selector/report_selector";
import { ReportEdge, Viewer } from "src/generated/types";

import { ReportPartialMatcher } from "./report_partial_matcher";

/*
 * GraphQL
 */

export const LIST_REPORTS_QUERY = gql`
    query ListReports {
        viewer {
            user {
                id
                school {
                    id
                    reports(where: { results: { exists: true } }, order: [year_DESC]) {
                        count
                        edges {
                            node {
                                id
                                year
                                type
                            }
                        }
                    }
                }
            }
        }
    }
`;

export const GET_REPORT_QUERY = gql`
    query GetReport($type: String!, $year: String!) {
        viewer {
            user {
                id
                school {
                    id
                    reports(where: { results: { exists: true }, type: { equalTo: $type }, year: { equalTo: $year } }) {
                        count
                        edges {
                            node {
                                id
                                year
                                type
                            }
                        }
                    }
                }
            }
        }
    }
`;

/*
 * Types
 */
type SectionPath =
    | "expertise-and-resources"
    | "overview"
    | "purpose"
    | "relationships"
    | "strength-and-growth-opportunities"
    | "teaching-and-learning"
    | "well-being";

interface ApolloQueryData {
    readonly viewer: Viewer;
}

interface ReportUrlPath {
    readonly type: string;
    readonly year: string;
    readonly section: SectionPath;
}

interface QueryVariables {
    readonly type: string;
    readonly year: string;
}

/*
 * Main
 */

export const ReportsPage = (): React.ReactElement => {
    const params = useParams<ReportUrlPath>();
    const type = params.type;
    const year = params.year ?? "0"; // "0" for View All Reports
    const [selectedReportId, setSelectReportId] = useState("*");

    // Get single report data
    const {
        data: report,
        loading,
        error,
        stopPolling,
    } = useQuery<ApolloQueryData, QueryVariables>(GET_REPORT_QUERY, {
        pollInterval: 1000,
        variables: { type, year },
    });

    useEffect(() => {
        let id = report?.viewer.user.school?.reports.edges?.[0]?.node?.id;

        // View all reports case
        if (year === "0" && !id) {
            id = type === "fsci" ? "viewAllFSCI" : "viewAllFFI";
        }

        setSelectReportId(id ?? "*");
    }, [report]);

    // Get reports drop down list data
    const { data: reports } = useQuery<ApolloQueryData, Empty>(LIST_REPORTS_QUERY);

    if (loading) return <Loading />;
    if (error) return <Error />;

    if (report?.viewer.user.school?.reports.edges?.length) {
        stopPolling();
    }

    return (
        <div className="main-page-wrap">
            <div className="center">
                {reports?.viewer.user.school?.reports.edges?.length ? (
                    <>
                        <ReportSelector
                            reports={(reports?.viewer.user.school?.reports.edges || []) as ReportEdge[]}
                            selectedReportType={type}
                            selectedReportYear={year}
                        />
                        {selectedReportId && selectedReportId !== "*" && <ReportPartialMatcher id={selectedReportId} />}
                    </>
                ) : (
                    <Loading>
                        <p>
                            Your report is being generated...This could take a while. The page will refresh
                            automatically, you can close this page and return at any time.
                        </p>
                    </Loading>
                )}
            </div>
        </div>
    );
};
